import type { CheckoutTimingTracker } from "./analytics";
import type { CardErrorCode } from "./penpal/connect-card";
import type { TunnelXIFrameConnection } from "./penpal/connect-tunnel-x";

export type PaymentKitEnvironment = "local" | "tunnel" | "sandbox" | "staging" | "production";

type EnvironmentUrls = {
  baseUrl: string;
  apiBaseUrl: string;
};

// TODO: Cleanup this after launch to only use SDK instead of plain fetch.
const STATIC_ENVIRONMENT_URLS: Record<Exclude<PaymentKitEnvironment, "tunnel">, EnvironmentUrls> = {
  local: {
    baseUrl: "http://localhost:9101",
    apiBaseUrl: "http://localhost:9000",
  },
  sandbox: {
    baseUrl: "https://app.paymentkit.com/customer",
    apiBaseUrl: "https://app.paymentkit.com",
  },
  staging: {
    baseUrl: "https://staging.paymentkit.com/customer",
    apiBaseUrl: "https://staging.paymentkit.com",
  },
  production: {
    baseUrl: "https://app.paymentkit.com/customer",
    apiBaseUrl: "https://app.paymentkit.com",
  },
};

function getTunnelUrls(): EnvironmentUrls {
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const apiBaseUrl = params.get("api_base_url");
    if (apiBaseUrl) {
      return {
        baseUrl: window.location.origin,
        apiBaseUrl,
      };
    }
    // For loclx-style tunnels where API hostname is derived from the current hostname
    const hostname = window.location.hostname;
    if (hostname.includes("loclx.io")) {
      const apiHostname = hostname.replace(/cportal|playground/, "api");
      return {
        baseUrl: `https://${hostname}`,
        apiBaseUrl: `https://${apiHostname}`,
      };
    }
  }
  // Fallback
  return STATIC_ENVIRONMENT_URLS.local;
}

export function getUrlsForEnvironment(environment: PaymentKitEnvironment): EnvironmentUrls {
  if (environment === "tunnel") {
    return getTunnelUrls();
  }
  const urls = STATIC_ENVIRONMENT_URLS[environment];
  if (!urls) {
    console.warn(`[PaymentKit] Invalid environment "${environment}", falling back to local`);
    return STATIC_ENVIRONMENT_URLS.local;
  }
  return urls;
}

type FormFieldNames =
  | "customer_name"
  | "customer_email"
  | "customer_country"
  | "customer_zip_code"
  | "customer_business_name"
  | "customer_address_line1"
  | "customer_address_line2"
  | "customer_city"
  | "customer_state"
  | "customer_tax_ids"
  | "shipping_address_line1"
  | "shipping_address_line2"
  | "shipping_city"
  | "shipping_state"
  | "shipping_zip_code"
  | "shipping_country";

export type FormErrorCodes = "required" | "invalid";

export type CardSubmitOptions = {
  skipCustomerValidation?: boolean;
};

export type TInternalFuncs = {
  submitPayment: (
    fields: Partial<PaymentKitFields>,
    options?: unknown,
  ) => Promise<{ data: { [key: string]: unknown }; errors?: never } | { data?: never; errors: PaymentKitErrors }>;
  cleanup?: () => void;
};

export type PaymentKit = <T extends readonly PaymentMethod<unknown>[]>(options: {
  environment?: PaymentKitEnvironment;
  secureToken: string;
  paymentMethods: T;
  /** Card tokenization mode from checkout session */
  cardTokenizationMode?: "direct" | "vgs";
  /** VGS vault ID from checkout session */
  vgsVaultId?: string;
  /** VGS environment from checkout session */
  vgsEnvironment?: string;
  /** @internal Enable analytics test mode for e2e testing */
  __enableAnalyticsTestMode?: boolean;
}) => ExternalFuncsMapByPm<T> & {
  submit: PaymentKitSubmitHandler<T>;
  cleanup: () => void;
};

type PaymentKitSubmitHandler<T extends readonly PaymentMethod<unknown>[]> = <
  N extends keyof ExternalFuncsMapByPm<T>,
>(options: {
  fields: Partial<PaymentKitFields>;
  paymentMethod: N;
  options?: unknown;
  onError: (error: PaymentKitErrors) => void;
  onSuccess: (data: { [key: string]: unknown }) => void;
}) => void;

export type PaymentKitStates = {
  baseUrl: string;
  apiBaseUrl: string;
  secureToken: string;
  environment: PaymentKitEnvironment;
  tunnelXConnection: TunnelXIFrameConnection;
  timingTracker: CheckoutTimingTracker;
  /** Checkout request ID for correlating all API calls and analytics events */
  checkoutRequestId: string;
  /** Card tokenization mode: 'direct' (KMS) or 'vgs' (VGS Collect + proxy) */
  cardTokenizationMode?: "direct" | "vgs";
  /** VGS vault ID (only when cardTokenizationMode is 'vgs') */
  vgsVaultId?: string;
  /** VGS environment: 'sandbox' or 'live' (only when cardTokenizationMode is 'vgs') */
  vgsEnvironment?: string;
  /** @internal Promise that resolves when session config (incl. VGS mode) is auto-detected */
  _sessionConfigReady?: Promise<void>;
};

export type PaymentKitErrors = {
  root?: string;
  card_pan?: CardErrorCode;
  card_exp?: CardErrorCode;
  card_cvc?: CardErrorCode;
  paypal?: string;
  google_pay?: string;
  apple_pay?: string;
  processor_id?: string;
  amount?: string;
  currency?: string;
  country?: string;
} & { [key in FormFieldNames]?: FormErrorCodes | string };

export type PaymentKitFields = { [key in FormFieldNames]: string };

export type PaymentMethod<TExternalFuncs = unknown, TName = string> = (paymentKitStates: PaymentKitStates) => {
  name: TName;
  externalFuncs: TExternalFuncs;
  internalFuncs: TInternalFuncs;
};

export type ExternalFuncsMapByPm<T extends readonly PaymentMethod<unknown>[]> = {
  [K in T[number] as ReturnType<K>["name"]]: ReturnType<K>["externalFuncs"];
};
